import React from "react";
import {StyledChip} from "../../components/chips";
import {
  GET_CONDITIONS,
  CREATE_CONDITION,
  UPDATE_CONDITION,
  SOFT_DELETE_CONDITION,
} from "../../graphql/schema/conditionQueries";
import {
  GET_OBLIGATION_TYPES,
  CREATE_OBLIGATION_TYPE,
  UPDATE_OBLIGATION_TYPE,
  SOFT_DELETE_OBLIGATION_TYPE,
} from "../../graphql/schema/obligationTypeQueries";
import {
  GET_TASK_TYPES,
  CREATE_TASK_TYPE,
  UPDATE_TASK_TYPE,
  SOFT_DELETE_TASK_TYPE,
} from "../../graphql/schema/taskTypeQueries";
import {
  GET_INCIDENT_TYPES,
  CREATE_INCIDENT_TYPE,
  UPDATE_INCIDENT_TYPE,
  SOFT_DELETE_INCIDENT_TYPE,
} from "../../graphql/schema/incidentTypeQueries";
import {
  GET_SEVERITIES,
  CREATE_INCIDENT_SEVERITY,
  UPDATE_INCIDENT_SEVERITY,
  SOFT_DELETE_INCIDENT_SEVERITY,
  DECREMENT_INCIDENT_SEVERITIES,
} from "../../graphql/schema/incidentSeverityQueries";
import {
  GET_INCIDENT_PHASES,
  CREATE_INCIDENT_PHASE,
  UPDATE_INCIDENT_PHASE,
  SOFT_DELETE_INCIDENT_PHASE,
  DECREMENT_INCIDENT_PHASES,
} from "../../graphql/schema/incidentPhaseQueries";
import {
  GET_ACTION_PHASES,
  CREATE_ACTION_PHASE,
  UPDATE_ACTION_PHASE,
  SOFT_DELETE_ACTION_PHASE,
  DECREMENT_ACTION_PHASES,
} from "../../graphql/schema/actionPhaseQueries";
import {CREATE_TAG, DELETE_TAG, GET_TAGS_FOR_SCHEMA, UPDATE_TAG} from "../../graphql/tagQueries";
import {
  ConditionsSchemaFields,
  ObligationTypesSchemaFields,
  IncidentTypesSchemaFields,
  IncidentSeveritySchemaFields,
  IncidentPhaseSchemaFields,
  ActionPhaseSchemaFields,
  TaskTypesSchemaFields,
  TagSchemaFields,
  MetricTypesSchemaFields,
} from "./SchemaFields";
import {tagTimesUsed} from "../../resources/tags";
import {ALERT} from "../../resources/environment";
import chroma from "chroma-js";
import {enumSeparator} from "../../resources/conditions";
import {EXERCISE_TYPE_NAME} from "../Incidents/Utils/incidentUtils";
import {actionColumn, getTableActions} from "../../components/table/TableActions";
import {ClickableNameCell} from "../../components/table/ClickableNameCell";
import {isEmpty, isNumber, isString, filter, find, map, pluck, reject, some} from "underscore";
import {
  CREATE_METRIC_TYPE,
  GET_METRIC_TYPES,
  SOFT_DELETE_METRIC_TYPE,
  UPDATE_METRIC_TYPE,
} from "../../graphql/schema/metricTypeQueries";

export const contrastValidator = (data) => {
  if (!data.backgroundColor && !data.foregroundColor) {
    return true;
  }
  if (!data.backgroundColor || !data.foregroundColor) {
    ALERT.error("Please make sure both background and text color are set");
    return false;
  }
  if (chroma.contrast(data.backgroundColor, data.foregroundColor) < 4) {
    ALERT.error("Please improve the contrast between the colors");
    return false;
  }
  return true;
};

export const schemaDetails = {
  conditions: {
    displayName: "Attributes / Condition",
    singularDisplayName: "Attribute / Condition",
    listTitleId: "customize-conditions-title",
  },
  obligationTypes: {
    displayName: "Procedure Type",
    listTitleId: "customize-procedure-types-title",
    blockDeletion: true,
  },
  types: {
    displayName: "Incident Type",
    listTitleId: "customize-incident-types-title",
    blockDeletion: true,
  },
  taskTypes: {
    displayName: "Task Type",
    listTitleId: "customize-task-types-title",
    blockDeletion: true,
  },
  incidentSeverities: {
    displayName: "Incident Severity",
    listTitleId: "customize-incident-severities-title",
    blockDeletion: true,
    prismaName: "incidentSeverity",
    orderingName: "updateOneIncidentSeverity",
    reverseOrder: true,
    topText: "Most Severe",
    bottomText: "Least Severe",
  },
  incidentPhases: {
    displayName: "Incident Phase",
    listTitleId: "customize-incident-phases-title",
    prismaName: "incidentPhase",
    orderingName: "updateOneIncidentPhase",
    topText: "First Phase",
    bottomText: "Last Phase",
  },
  actionPhases: {
    displayName: "Action Phase",
    listTitleId: "customize-action-phases-title",
    prismaName: "actionPhase",
    orderingName: "updateOneActionPhase",
    topText: "First Phase",
    bottomText: "Last Phase",
  },
  tags: {
    displayName: "Tag",
    listTitleId: "customize-tags-title",
  },
  metricTypes: {
    displayName: "Metric Type",
    listTitleId: "customize-metric-types-title",
  },
};

export const getSchemaDetailsForMenu = (metricsInIncidents) => {
  const schemaDetailsForMenu = {...schemaDetails};
  if (!metricsInIncidents) {
    delete schemaDetailsForMenu.metricTypes;
  }
  return schemaDetailsForMenu;
};

export const dynamicFormQueryMap = {
  conditions: {
    create: CREATE_CONDITION,
    update: UPDATE_CONDITION,
    softDelete: SOFT_DELETE_CONDITION,
    refetch: GET_CONDITIONS,
    formDefaults: {
      field: "",
      fieldType: null,
      listOptions: [{name: "", description: ""}],
    },
    formFieldsComponent: <ConditionsSchemaFields />,
  },
  obligationTypes: {
    create: CREATE_OBLIGATION_TYPE,
    update: UPDATE_OBLIGATION_TYPE,
    softDelete: SOFT_DELETE_OBLIGATION_TYPE,
    refetch: GET_OBLIGATION_TYPES,
    formDefaults: {
      name: "",
      description: "",
      backgroundColor: null,
      foregroundColor: null,
    },
    formFieldsComponent: <ObligationTypesSchemaFields />,
  },
  types: {
    create: CREATE_INCIDENT_TYPE,
    update: UPDATE_INCIDENT_TYPE,
    softDelete: SOFT_DELETE_INCIDENT_TYPE,
    refetch: GET_INCIDENT_TYPES,
    formDefaults: {
      name: "",
      description: "",
      backgroundColor: null,
      foregroundColor: null,
    },
    formFieldsComponent: <IncidentTypesSchemaFields />,
  },
  taskTypes: {
    create: CREATE_TASK_TYPE,
    update: UPDATE_TASK_TYPE,
    softDelete: SOFT_DELETE_TASK_TYPE,
    refetch: GET_TASK_TYPES,
    formDefaults: {
      name: "",
      description: "",
      foregroundColor: null,
      backgroundColor: null,
    },
    formFieldsComponent: <TaskTypesSchemaFields />,
  },
  incidentSeverities: {
    create: CREATE_INCIDENT_SEVERITY,
    update: UPDATE_INCIDENT_SEVERITY,
    softDelete: SOFT_DELETE_INCIDENT_SEVERITY,
    refetch: GET_SEVERITIES,
    decrement: DECREMENT_INCIDENT_SEVERITIES,
    formDefaults: {
      name: "",
      description: "",
      backgroundColor: null,
      foregroundColor: null,
    },
    formFieldsComponent: <IncidentSeveritySchemaFields />,
  },
  incidentPhases: {
    create: CREATE_INCIDENT_PHASE,
    update: UPDATE_INCIDENT_PHASE,
    softDelete: SOFT_DELETE_INCIDENT_PHASE,
    refetch: GET_INCIDENT_PHASES,
    decrement: DECREMENT_INCIDENT_PHASES,
    formDefaults: {
      name: "",
      description: "",
      backgroundColor: null,
      foregroundColor: null,
    },
    formFieldsComponent: <IncidentPhaseSchemaFields />,
  },
  actionPhases: {
    create: CREATE_ACTION_PHASE,
    update: UPDATE_ACTION_PHASE,
    softDelete: SOFT_DELETE_ACTION_PHASE,
    refetch: GET_ACTION_PHASES,
    decrement: DECREMENT_ACTION_PHASES,
    formDefaults: {
      name: "",
      description: "",
      backgroundColor: null,
      foregroundColor: null,
    },
    formFieldsComponent: <ActionPhaseSchemaFields />,
  },
  tags: {
    create: CREATE_TAG,
    update: UPDATE_TAG,
    softDelete: DELETE_TAG,
    refetch: GET_TAGS_FOR_SCHEMA,
    formDefaults: {name: ""},
    formFieldsComponent: <TagSchemaFields />,
  },
  metricTypes: {
    create: CREATE_METRIC_TYPE,
    update: UPDATE_METRIC_TYPE,
    softDelete: SOFT_DELETE_METRIC_TYPE,
    refetch: GET_METRIC_TYPES,
    formDefaults: {
      name: "",
      description: "",
    },
    formFieldsComponent: <MetricTypesSchemaFields />,
  },
};

export const conditionFieldTypeDisplay = (conditionType) => {
  switch (conditionType) {
    case "Enum":
      return "List";
    case "Boolean":
      return "True / False";
    case "Int":
      return "Number";
    default:
      return conditionType;
  }
};

export const metricMeasureAgainstDisplay = (measureAgainst, responseWorkflowName) => {
  switch (measureAgainst) {
    case "IncidentCreated":
      return `${responseWorkflowName?.upper ?? "Incident"} Created`;
    case "IncidentClosed":
      return `${responseWorkflowName?.upper ?? "Incident"} Closed`;
    case "SpecificEvent":
      return "Specific Event";
    default:
      return measureAgainst;
  }
};

export const getSchemaItemNameField = (schemaType) =>
  schemaType === "conditions" ? "field" : "name";

const getExistingSchemaItemName = (schemaType, item) => {
  if (schemaType === "conditions") {
    return item?.field;
  }
  if (schemaType === "tags") {
    return item?.value;
  }
  return item?.name;
};

export const trimSchemaItemName = (schemaType, data) => {
  const nameField = getSchemaItemNameField(schemaType);
  if (!isString(data?.[nameField])) {
    return data;
  }
  return {...data, [nameField]: data[nameField].trim()};
};

export const isDuplicateSchemaItemName = (schemaType, schemaItems, name, currentId) => {
  if (!isString(name)) {
    return false;
  }
  const normalizedName = name.trim().toLowerCase();
  if (isEmpty(normalizedName)) {
    return false;
  }
  return some(schemaItems, (item) => {
    const existingName = getExistingSchemaItemName(schemaType, item);
    return (
      !item?.isDeleted &&
      item?.id !== currentId &&
      isString(existingName) &&
      existingName.trim().toLowerCase() === normalizedName
    );
  });
};

export const getColumnsByType = (
  schemaType,
  table,
  setSelectedItems,
  setFormOpen,
  setIsAdd,
  onDelete,
  responseWorkflowName,
) => {
  const handleOpen = (rowId) => {
    if (!isNumber(rowId)) {
      return;
    }
    setSelectedItems && setSelectedItems([rowId]);
    setIsAdd && setIsAdd(false);
    setFormOpen(true);
  };

  const tableActions = {
    field: "actions",
    type: "actions",
    headerName: " ",
    minWidth: 75,
    ...actionColumn(),
    getActions: (params) => {
      const isExerciseType = schemaType === "types" && params?.row?.name === EXERCISE_TYPE_NAME;
      return getTableActions({
        showActions: table?.hoverRow === params?.row?.id,
        edit: () => handleOpen(params?.row?.id),
        del: isExerciseType ?
          undefined :
          () => {
            setSelectedItems && setSelectedItems([params?.row?.id]);
            onDelete && onDelete();
          },
      });
    },
  };

  switch (schemaType) {
    case "conditions":
      return [
        {
          field: "field",
          headerName: "Field",
          flex: 0.25,
          renderCell: (params) => (
            <ClickableNameCell name={params?.value} onClick={() => handleOpen(params?.row?.id)} />
          ),
        },
        {
          field: "fieldType",
          headerName: "Field Type",
          flex: 0.15,
          renderCell: (params) => conditionFieldTypeDisplay(params?.value),
        },
        {
          field: "listOptions",
          headerName: "Options",
          flex: 0.25,
          valueGetter: (value, row) => {
            return row?.listOptions?.map(({name}) => name).join(", ");
          },
        },
        {...tableActions},
      ];
    case "obligationTypes":
    case "types":
    case "taskTypes":
      return [
        {
          field: "name",
          headerName: "Type Name",
          width: 300,
          renderCell: (params) => (
            <ClickableNameCell
              name={<StyledChip data={params.row} defaultLabel="Custom" />}
              onClick={() => handleOpen(params?.row?.id)}
            />
          ),
          valueGetter: (value, row) => row?.name,
        },
        {field: "description", headerName: "Description", flex: 1},
        {...tableActions},
      ];
    case "incidentSeverities":
    case "incidentPhases":
    case "actionPhases":
      return [{field: "name", headerName: "Phase Name", flex: 0.25}];
    case "tags":
      return [
        {
          field: "name",
          headerName: "Tag Name",
          flex: 0.75,
          renderCell: (params) => (
            <ClickableNameCell
              name={params?.row?.value}
              onClick={() => handleOpen(params?.row?.id)}
            />
          ),
          valueGetter: (value, row) => row?.value,
        },
        {
          field: "timesUsed",
          headerName: "Times Used",
          flex: 0.25,
          headerAlign: "center",
          align: "center",
          valueGetter: (value, row) => tagTimesUsed(row),
        },
        {...tableActions},
      ];
    case "metricTypes":
      return [
        {
          field: "name",
          headerName: "Metric Name",
          flex: 0.6,
          renderCell: (params) => (
            <ClickableNameCell
              name={<StyledChip data={params.row} defaultLabel="Custom" />}
              onClick={() => handleOpen(params?.row?.id)}
            />
          ),
          valueGetter: (value, row) => row?.name,
        },
        {field: "description", headerName: "Description", flex: 1},
        {
          field: "measureAgainst",
          headerName: "Measure Against",
          flex: 0.5,
          valueGetter: (value, row) =>
            metricMeasureAgainstDisplay(row?.measureAgainst, responseWorkflowName),
        },
        {...tableActions},
      ];
    default:
      return [];
  }
};

export const populateFromDB = (schemaType, itemData, responseWorkflowName) => {
  switch (schemaType) {
    case "conditions":
      return {
        field: itemData.field,
        fieldType: {
          fieldType: conditionFieldTypeDisplay(itemData.fieldType),
          name: itemData.fieldType,
        },
        existingOptions: itemData.listOptions,
        listOptions: [],
      };
    case "taskTypes":
    case "obligationTypes":
    case "types":
      return {
        name: itemData.name,
        description: itemData.description,
        backgroundColor: itemData.backgroundColor,
        foregroundColor: itemData.foregroundColor,
        resetBackgroundColor: itemData.backgroundColor,
        resetForegroundColor: itemData.foregroundColor,
      };
    case "incidentSeverities":
    case "incidentPhases":
    case "actionPhases":
      return {
        name: itemData.name,
        description: itemData.description,
        backgroundColor: itemData.backgroundColor,
        foregroundColor: itemData.foregroundColor,
        resetBackgroundColor: itemData.backgroundColor,
        resetForegroundColor: itemData.foregroundColor,
      };
    case "tags":
      return {name: itemData.value};
    case "metricTypes":
      return {
        name: itemData.name,
        description: itemData.description,
        measureAgainst: {
          name: itemData.measureAgainst,
          display: metricMeasureAgainstDisplay(itemData.measureAgainst, responseWorkflowName),
        },
      };
    default:
      console.error(schemaType, "schemaType not supported");
  }
};

const diffListOptions = (existingOptions, newOptions) => {
  const optionsToCreate = filter(newOptions, (option) => !option.id);
  const optionsToUpdate = map(
    filter(newOptions, (option) => {
      if (!option.id) {
        return false;
      }
      const existing = find(existingOptions, (existingData) => existingData.id === option.id);
      return (
        existing && (existing.name !== option.name || existing.description !== option.description)
      );
    }),
    (option) => ({
      where: {id: option.id},
      data: {
        name: {set: option.name},
        description: {set: option.description},
      },
    }),
  );
  const optionsToDelete = pluck(
    reject(existingOptions, (existing) =>
      find(newOptions, (newOption) => newOption.id === existing.id),
    ),
    "id",
  );

  return {
    optionsToCreate,
    optionsToUpdate,
    optionsToDelete,
  };
};

export const populateForSubmit = (schemaType, data, isAdd, schemaCount, itemData) => {
  switch (schemaType) {
    case "conditions":
      if (data.fieldType.name === "Enum") {
        const options = data.listOptions ?
          data.listOptions.map((option) => {
            const optionName = option.name.replaceAll(enumSeparator, "").trim();
            return {name: optionName, description: option.description};
          }) :
          [];
        if (isAdd) {
          return {
            field: data.field,
            fieldType: data.fieldType.name,
            listOptions: options,
          };
        }
        const {optionsToCreate, optionsToUpdate, optionsToDelete} = diffListOptions(
          itemData.listOptions,
          data.listOptions,
        );
        return {
          conditionId: itemData.id,
          field: data.field,
          fieldType: data.fieldType.name,
          optionsToCreate: optionsToCreate,
          optionsToUpdate: optionsToUpdate,
          optionsToDelete: optionsToDelete,
        };
      }

      if (isAdd) {
        return {
          field: data.field,
          fieldType: data.fieldType.name,
          listOptions: [],
        };
      }

      const enumOptionsToClear = !isEmpty(itemData?.listOptions) ?
        itemData.listOptions.map((option) => option.id) :
        [];

      return {
        conditionId: itemData.id,
        field: data.field,
        fieldType: data.fieldType.name,
        optionsToCreate: [],
        optionsToUpdate: [],
        optionsToDelete: enumOptionsToClear,
      };
    case "obligationTypes":
    case "taskTypes":
    case "types":
      return isAdd ?
        {
          name: data.name,
          description: data.description,
          backgroundColor: data.backgroundColor,
          foregroundColor: data.foregroundColor,
        } :
        {
          id: itemData.id,
          name: data.name,
          description: data.description,
          backgroundColor: data.backgroundColor ?? null,
          foregroundColor: data.foregroundColor ?? null,
        };
    case "incidentSeverities":
    case "incidentPhases":
    case "actionPhases":
      return isAdd ?
        {
          name: data.name,
          description: data.description,
          ordering: schemaCount + 1,
          backgroundColor: data.backgroundColor,
          foregroundColor: data.foregroundColor,
        } :
        {
          id: itemData.id,
          name: data.name,
          description: data.description,
          backgroundColor: data.backgroundColor,
          foregroundColor: data.foregroundColor,
        };
    case "tags":
      return isAdd ? {tag: data.name} : {id: itemData.id, tag: data.name};
    case "metricTypes":
      return isAdd ?
        {
          name: data.name,
          description: data.description,
          measureAgainst: data.measureAgainst,
        } :
        {
          id: itemData.id,
          name: data.name,
          description: data.description,
          measureAgainst: data.measureAgainst,
        };
    default:
      console.error(schemaType, "schemaType not supported");
  }
};
